<template>
	<div class="grid-flex-container mx-2 pt-6" v-if="canView">
		<div class="d-flex" style="overflow-y: hidden">
			<div style="width: 40%" class="column mr-2">
				<v-container fluid class="pa-0">
					<div class="searchRow">
						<div style="flex-grow: 1">
							<v-text-field
								v-model="clearListTrigger"
								v-debounce:1000ms="onTypeSearchChanged"
								append-icon="mdi-magnify"
								label="Search"
								class="py-0 my-0 mb-2 mr-2"
								single-line
								hide-details
								clearable
								@click:clear="() => onTypeSearchChanged('')"
							></v-text-field>
						</div>
						<v-btn
							v-if="canAddReferenceType"
							color="primary"
							@click.stop="addReferenceListType"
							class="mb-2 pl-4 pr-6 float-right"
						>
							<v-icon> mdi-plus</v-icon>
							&nbsp;&nbsp;Add
						</v-btn>
					</div>
				</v-container>
				<div style="flex-grow: 1; overflow-y: hidden">
					<v-list dense style="height: 100%">
						<v-list-item-group
							style="overflow-y: scroll; height: 100%"
							v-model="selectedReferenceListType"
							active-class="primary white--text"
							mandatory
						>
							<v-list-item
								v-for="(item, i) in filteredItems"
								:key="i"
								><v-list-item-title>{{
									item.Name
								}}</v-list-item-title>
								<v-list-item-action @click.stop>
									<v-btn
										v-if="canEditReferenceType"
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click.stop="
											editReferenceListType(item)
										"
									>
										<v-icon small color="primary"
											>mdi-pencil</v-icon
										>
									</v-btn>
								</v-list-item-action>
								<v-list-item-action
									class="ml-1 my-0 mr-0"
									@click.stop
								>
									<v-btn
										v-if="canDeleteReferenceType"
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click.stop="
											deleteReferenceListType(item)
										"
									>
										<v-icon small color="error"
											>mdi-delete</v-icon
										>
									</v-btn>
								</v-list-item-action>
							</v-list-item>
						</v-list-item-group>
					</v-list>
				</div>
			</div>
			<div style="width: 60%" class="column">
				<v-container fluid class="pa-0">
					<div class="searchRow">
						<v-text-field
							v-model="searchValues"
							append-icon="mdi-magnify"
							label="Search"
							class="py-0 my-0 mb-2 mr-2"
							single-line
							hide-details
							clearable
						></v-text-field>
						<v-btn
							v-if="canAddReferenceValues"
							color="primary"
							@click.stop="addReferenceListValue"
							class="mb-2 pl-4 pr-6"
						>
							<v-icon> mdi-plus</v-icon>
							&nbsp;&nbsp;Add
						</v-btn>
					</div>
				</v-container>
				<div class="data-table">
					<v-data-table
						dense
						:headers="headers"
						:search="searchValues"
						:items="listItems"
						:disable-pagination="true"
						sort-by="Order"
						item-key="Id"
						fixed-header
						:loading="loading || isLoading"
						loading-text="Loading..."
						hide-default-footer
					>
						<template v-slot:body="props">
							<draggable
								@change="reorder(props.items)"
								:list="props.items"
								tag="tbody"
							>
								<tr
									v-for="(item, index) in props.items"
									:key="index"
								>
									<td>
										<v-icon small color="primary"
											>mdi-reorder-horizontal</v-icon
										>
									</td>

									<td>{{ item.Value }}</td>
									<td>{{ item.TypeName }}</td>
									<td>
										<v-btn
											v-if="canDeleteReferenceValues"
											fab
											small
											icon
											elevation="1"
											class="action-btn float-right"
											@click.stop="
												deleteReferenceListValue(item)
											"
										>
											<v-icon color="error" small
												>mdi-delete</v-icon
											>
										</v-btn>
										<v-btn
											v-if="canEditReferenceValues"
											fab
											small
											icon
											elevation="1"
											class="action-btn float-right"
											@click.stop="
												editReferenceListValue(item)
											"
										>
											<v-icon color="primary" small
												>mdi-pencil</v-icon
											>
										</v-btn>
									</td>
								</tr>
							</draggable>
						</template>
					</v-data-table>
				</div>
			</div>
		</div>

		<reference-type-dialog
			v-if="showReferenceTypeDialog"
			:item="selectedReferenceType"
			:action="selectedReferenceTypeAction"
			:showDialog="showReferenceTypeDialog"
			@close="onReferenceTypeDialogClose"
		/>
		<reference-list-value-dialog
			v-if="
				showReferenceListValueDialog &&
				filteredItems[selectedReferenceListType]
			"
			:item="selectedReferenceListValue"
			:type="filteredItems[selectedReferenceListType]"
			:action="selectedReferenceListValueAction"
			:showDialog="showReferenceListValueDialog"
			@close="onReferenceListValueDialogClose"
		/>
	</div>
</template>
<script>
import { mapGetters } from 'vuex';
import { ReferenceListsGetters } from '../../../store/getters';
import { ReferenceListsActions, GeneralActions } from '../../../store/actions';
import { Permissions } from '../../../enums/permissionsEnum';
import ReferenceTypeDialog from './ReferenceTypeDialog';
import ReferenceListValueDialog from './ReferenceListValueDialog';
import permissionsMixin from '../../../mixins/permissionsMixin';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import Draggable from 'vuedraggable';
export default {
	mixins: [permissionsMixin],
	name: 'ReferenceLists',
	components: {
		ReferenceTypeDialog,
		ReferenceListValueDialog,
		Draggable,
	},
	computed: {
		...mapGetters({
			types: [ReferenceListsGetters.REFERENCE_LIST_TYPES],
			listItems: [ReferenceListsGetters.REFERENEC_LIST_VALUES],
			loading: [ReferenceListsGetters.GRID_LOADING],
		}),
		filteredItems() {
			if (!this.searchType) return this.types;
			return this.types.filter((x) => {
				return (
					x.Name &&
					x.Name.toLowerCase().includes(this.searchType.toLowerCase())
				);
			});
		},
		hasSelectedReferenceType() {
			if (this.selectedReferenceListType) return false;
			return true;
		},
		canView() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.View
			);
		},
		canAddReferenceType() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceType.Add
			);
		},
		canEditReferenceType() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceType.Edit
			);
		},
		canDeleteReferenceType() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceType.Edit
			);
		},
		canAddReferenceValues() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceValues.Add
			);
		},
		canEditReferenceValues() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceValues.Edit
			);
		},
		canDeleteReferenceValues() {
			return this.hasPermissions(
				Permissions.Settings.ReferenceLists.ReferenceValues.Edit
			);
		},
	},
	data() {
		return {
			selectedReferenceListType: null,
			reorderedValues: [],
			searchValues: '',
			searchType: '',
			clearListTrigger: '',
			isLoading: false,
			headers: [
				{ text: '', sortable: false, value: 'Draggable', width: 10 },

				{
					text: 'Reference List Value',
					value: 'Value',
					sortable: false,
				},
				{
					text: 'Reference List Type',
					value: 'TypeName',
					sortable: false,
				},
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
			confirmShow: false,
			confirmMessage: '',
			confirmPromise: null,

			showReferenceTypeDialog: false,
			selectedReferenceType: null,
			selectedReferenceTypeAction: null,

			showReferenceListValueDialog: false,
			selectedReferenceListValue: null,
			selectedReferenceListValueAction: null,
		};
	},
	watch: {
		selectedReferenceListType(val) {
			if (this.filteredItems[val]) {
				this.$store
					.dispatch(ReferenceListsActions.CLEAR_REFERENCE_LIST_VALUES)
					.then(() => {
						this.$store.dispatch(
							ReferenceListsActions.LOAD_SELECTED_REFERENCE_LIST_TYPE,
							this.filteredItems[val]
						);
					});
			}
		},
		searchType() {
			this.selectedReferenceListType = null;
		},
		clearListTrigger(val) {
			if (val == null) this.isLoading = false;
			else this.isLoading = true;
			this.$store.dispatch(
				ReferenceListsActions.CLEAR_REFERENCE_LIST_VALUES
			);
		},
	},
	created() {
		this.$store.dispatch(GeneralActions.LOADING, true);
		this.$store
			.dispatch(ReferenceListsActions.LOAD_REFERENCE_LIST_TYPES)
			.then(() => {
				this.$store.dispatch(GeneralActions.LOADING, false);
				if (this.filteredItems.length > 0)
					this.selectedReferenceListType = 0;
			});
	},
	destroyed() {
		this.$store.dispatch(ReferenceListsActions.CLEAR);
	},
	methods: {
		onTypeSearchChanged(props) {
			this.searchType = props;
			this.isLoading = false;
		},
		reorder(props) {
			this.reorderedValues = props;
			this.reorderedValues.forEach((element) => {
				element.Order = this.reorderedValues.indexOf(element) + 1;
			});
			this.$store.dispatch(
				ReferenceListsActions.REORDER_REFERENCE_LIST_VALUES,
				this.reorderedValues
			);
		},
		deleteReferenceListType(item) {
			ConfirmDialog({
				message:
					'Are you sure you want to delete this Reference List Type?',
			}).then((result) => {
				if (result) {
					this.$store.dispatch(
						ReferenceListsActions.DELETE_REFERENCE_LIST_TYPE,
						item
					);
				}
			});
		},
		deleteReferenceListValue(item) {
			ConfirmDialog({
				message:
					'Are you sure you want to delete this Reference Value?',
			}).then((result) => {
				if (result) {
					this.$store
						.dispatch(
							ReferenceListsActions.DELETE_REFERENCE_LIST_VALUE,
							item
						)
						.then(() => {
							this.reorder(this.listItems);
						});
				}
			});
		},
		addReferenceListValue() {
			this.selectedReferenceListValueAction = 'add';
			this.showReferenceListValueDialog = true;
		},
		editReferenceListValue(item) {
			this.selectedReferenceListValueAction = 'edit';
			this.selectedReferenceListValue = item;
			this.showReferenceListValueDialog = true;
		},
		onReferenceListValueDialogClose() {
			this.selectedReferenceListValue = null;
			this.selectedReferenceListValueAction = null;
			this.showReferenceListValueDialog = false;
		},
		addReferenceListType() {
			this.selectedReferenceTypeAction = 'add';
			this.showReferenceTypeDialog = true;
		},
		editReferenceListType(item) {
			this.selectedReferenceTypeAction = 'edit';
			this.selectedReferenceType = item;
			this.showReferenceTypeDialog = true;
		},
		onReferenceTypeDialogClose() {
			this.selectedReferenceType = null;
			this.selectedReferenceTypeAction = null;
			this.showReferenceTypeDialog = false;
		},
	},
};
</script>
<style scoped>
.data-table {
	flex-grow: 1;
	overflow-y: hidden;
}
.searchRow {
	display: flex;
	flex-direction: row;
}
.column {
	display: flex;
	overflow-y: hidden;
	height: 100%;
	flex-direction: column;
}
</style>
